// Computes a text summary of recent training load, CTL/ATL/TSB metrics,
// and gear usage. Used to enrich AI chat context.

import { computeWeeklyLoad } from './weeklyLoad';

// Return YYYY-MM-DD in local time — avoids UTC drift when mixing local midnight
// Dates with toISOString() (which is always UTC).
function localDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const computeTrainingSummary = (workouts, athleteGear = {}, gearTags = {}) => {
  if (!workouts || workouts.length === 0) return null;

  const now = new Date();
  const weeksAgo = (n) => new Date(now - n * 7 * 24 * 60 * 60 * 1000);

  const inWindow = (list, since) => list.filter(w => new Date(w.date) >= since);

  const summariseByType = (list) => {
    const result = {};
    list.forEach(w => {
      const t = w.type || 'other';
      if (!result[t]) result[t] = { count: 0, durationMin: 0, distanceKm: 0 };
      result[t].count++;
      result[t].durationMin += Math.round((w.duration || 0) / 60);
      result[t].distanceKm += parseFloat(((w.distance || 0) / 1000).toFixed(1));
    });
    return result;
  };

  const w4 = inWindow(workouts, weeksAgo(4));
  const w2 = inWindow(workouts, weeksAgo(2));
  const summary4 = summariseByType(w4);
  const summary2 = summariseByType(w2);

  const formatSummary = (s) =>
    Object.entries(s)
      .map(([type, d]) => `${type}: ${d.count} sessions, ${d.durationMin}min, ${d.distanceKm}km`)
      .join(' | ');

  let text = `Training load summary:`;
  text += `\n- Last 4 weeks (${w4.length} sessions): ${formatSummary(summary4) || 'none'}`;
  text += `\n- Last 2 weeks (${w2.length} sessions): ${formatSummary(summary2) || 'none'}`;

  // CTL / ATL / TSB — warm up over 180 days (full Strava fetch window) so
  // scores match the Dashboard gauge. A 42-day-only warmup starts from CTL=0
  // and produces artificially low fitness scores for established athletes.
  const k42 = 1 - Math.exp(-1 / 42);
  const k7  = 1 - Math.exp(-1 / 7);
  const tssByDay = {};
  workouts.forEach(w => {
    const d = w.date.slice(0, 10);
    tssByDay[d] = (tssByDay[d] || 0) + (w.tss || 0);
  });
  const today = new Date(); today.setHours(0, 0, 0, 0);
  let ctl = 0, atl = 0;
  let ctlWeekAgo = 0, atlWeekAgo = 0;
  let ctlFourWeeksAgo = 0;
  for (let i = 179; i >= 0; i--) {
    const d = new Date(today); d.setDate(d.getDate() - i);
    const tss = tssByDay[localDateStr(d)] || 0;
    ctl = ctl + (tss - ctl) * k42;
    atl = atl + (tss - atl) * k7;
    if (i === 7)  { ctlWeekAgo = ctl; atlWeekAgo = atl; }
    if (i === 28) { ctlFourWeeksAgo = ctl; }
  }
  const tsb = ctl - atl;
  const tsbWeekAgo = ctlWeekAgo - atlWeekAgo;
  const ctlWeekDelta  = +(ctl - ctlWeekAgo).toFixed(1);
  const atlWeekDelta  = +(atl - atlWeekAgo).toFixed(1);
  const tsbWeekDelta  = +(tsb - tsbWeekAgo).toFixed(1);
  const ctl4wDelta    = +(ctl - ctlFourWeeksAgo).toFixed(1);

  const tsbLabel = tsb > 10 ? 'fresh / ready to perform'
    : tsb > -10 ? 'neutral'
    : tsb > -20 ? 'productive fatigue'
    : 'high fatigue — recovery recommended';

  const trend = ctl4wDelta > 5 ? 'building' : ctl4wDelta < -5 ? 'declining' : 'maintaining';

  text += `\n\nFitness · Fatigue · Form (based on TSS/training load, last 6 weeks):`;
  text += `\n- Fitness (CTL): ${ctl.toFixed(1)}  (${ctlWeekDelta >= 0 ? '+' : ''}${ctlWeekDelta} this week, ${ctl4wDelta >= 0 ? '+' : ''}${ctl4wDelta} over 4 weeks — ${trend})`;
  text += `\n- Fatigue (ATL): ${atl.toFixed(1)}  (${atlWeekDelta >= 0 ? '+' : ''}${atlWeekDelta} this week)`;
  text += `\n- Form   (TSB): ${tsb.toFixed(1)}  (${tsbWeekDelta >= 0 ? '+' : ''}${tsbWeekDelta} this week — ${tsbLabel})`;
  text += `\nNote: scores use a 180-day warmup window so CTL/ATL are fully converged for established athletes.`;

  // Weekly training load — last 8 weeks vs ACWR-derived optimal band (0.8x-1.3x of prior 4-week average)
  const weekly = computeWeeklyLoad(workouts, { weeks: 8 });
  if (weekly.length > 0 && weekly.some(w => w.totalTSS > 0)) {
    text += `\n\nWeekly training load (last 8 weeks, TSS sum vs optimal range):`;
    weekly.forEach(w => {
      const range = w.optimalLow != null ? `optimal ${w.optimalLow}-${w.optimalHigh}` : 'building baseline';
      const tag = w.isCurrentWeek ? ' *current week, in progress*' : '';
      const statusLabel = w.status === 'optimal' ? 'in range'
        : w.status === 'over' ? 'over band'
        : w.status === 'under' ? 'under band'
        : 'n/a';
      text += `\n- ${w.weekLabel}: ${w.totalTSS} TSS (${statusLabel}, ${range})${tag}`;
    });
    const counts = weekly.reduce((acc, w) => { acc[w.status] = (acc[w.status] || 0) + 1; return acc; }, {});
    const summaryParts = [];
    if (counts.optimal) summaryParts.push(`${counts.optimal} in range`);
    if (counts.under)   summaryParts.push(`${counts.under} under`);
    if (counts.over)    summaryParts.push(`${counts.over} over`);
    if (counts.unknown) summaryParts.push(`${counts.unknown} building baseline`);
    text += `\nSummary: ${summaryParts.join(', ')}.`;
  }

  // Gear context
  const allGear = [...(athleteGear.bikes || []), ...(athleteGear.shoes || [])];
  const gearLines = allGear
    .map(item => {
      const tag = gearTags[item.id] || {};
      const isShoe = (athleteGear.shoes || []).some(s => s.id === item.id);
      const sessions = w4.filter(w => w.gear === item.id);
      if (!sessions.length) return null;
      const km = (sessions.reduce((s, w) => s + (w.distance || 0), 0) / 1000).toFixed(1);
      const typeLabel = tag.type ? ` [${tag.type} ${isShoe ? 'shoes' : 'bike'}]` : '';
      const note = tag.type === 'commute'
        ? ' — heavier bike, pace/speed likely understates actual effort'
        : tag.notes ? ` — ${tag.notes}` : '';
      return `- ${item.name}${typeLabel}: ${sessions.length} session${sessions.length !== 1 ? 's' : ''}, ${km}km${note}`;
    })
    .filter(Boolean);
  if (gearLines.length) {
    text += `\n\nGear usage (last 4 weeks):\n${gearLines.join('\n')}`;
  }

  return text;
};
